import { motion } from "framer-motion";
import { Star, Zap, Eye, ArrowUpRight, Loader2, Bookmark, TrendingUp, TrendingDown, Quote } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, getRef, getReward } from "../../lib/api";
import { useLiveMarket } from "../../context/LiveMarketContext";
import { useWatchlist } from "../../context/WatchlistContext";
import { Sparkline } from "./Sparkline";

export const ProductCard = ({ product, index, onPreview }) => {
  const { prices, dirs, history } = useLiveMarket();
  const { has, toggle } = useWatchlist();
  const live = prices[product.id];
  const pricing = live || product.pricing || {};
  const dir = dirs[product.id];
  const isBest = pricing.best_market_value;
  const isFree = product.base_price === 0;
  const watched = has(product.id);
  const [buying, setBuying] = useState(false);

  const buy = async () => {
    if (isFree) {
      toast.success("Free template unlocked", { description: `${product.title} is ready to download.` });
      return;
    }
    setBuying(true);
    try {
      const r = await api.checkout(product.id, window.location.origin, getRef(), getReward());
      if (r.checkout_url) window.location.href = r.checkout_url;
      else toast.success(r.message || "Unlocked");
    } catch {
      toast.error("Could not start checkout");
      setBuying(false);
    }
  };

  const flashClass = dir === "up" ? "text-emerald-400" : dir === "down" ? "text-red-400" : "text-slate-500";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
      data-testid={`product-card-${product.id}`}
      className="group relative flex flex-col border border-white/10 bg-slate-900 transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-emerald-500/40"
    >
      {isBest && (
        <>
          <div className="absolute inset-x-0 top-0 z-20 h-[2px] tracing-beam" />
          <div
            data-testid={`best-value-badge-${product.id}`}
            className="absolute right-3 top-3 z-20 flex items-center gap-1.5 bg-emerald-500 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-950"
          >
            <Zap className="h-3 w-3" /> Best Market Value
          </div>
        </>
      )}

      {/* Image */}
      <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10">
        <img
          src={product.image_url}
          alt={product.title}
          className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent" />
        <button
          data-testid={`watch-btn-${product.id}`}
          onClick={() => {
            toggle(product.id);
            toast[watched ? "message" : "success"](watched ? "Removed from watchlist" : "Added to watchlist", {
              description: watched ? product.title : `We'll flag ${product.title} when the price drops.`,
            });
          }}
          aria-label="Toggle watchlist"
          className={`absolute left-3 top-3 z-20 flex h-8 w-8 items-center justify-center border backdrop-blur transition-colors duration-200 ${
            watched
              ? "border-emerald-500 bg-emerald-500 text-slate-950"
              : "border-white/20 bg-slate-950/60 text-white hover:border-emerald-400"
          }`}
        >
          <Bookmark className={`h-4 w-4 ${watched ? "fill-slate-950" : ""}`} />
        </button>
        <span className="absolute bottom-3 left-3 border border-white/20 bg-slate-950/70 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-slate-200 backdrop-blur">
          {product.software_type}
        </span>
        <button
          data-testid={`preview-btn-${product.id}`}
          onClick={() => onPreview(product)}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 border border-white/20 bg-slate-950/70 px-2.5 py-1 text-xs text-white opacity-0 backdrop-blur transition-opacity duration-200 hover:border-emerald-400 group-hover:opacity-100"
        >
          <Eye className="h-3.5 w-3.5" /> Preview
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {product.tier} · {product.category}
          </span>
          <span data-testid={`rating-${product.id}`} className="flex items-center gap-1 font-mono text-xs text-slate-300">
            <Star className="h-3 w-3 fill-emerald-400 text-emerald-400" />
            {product.review_count > 0 ? product.user_rating : product.rating}
            {product.review_count > 0 && (
              <span className="text-slate-500">({product.review_count})</span>
            )}
          </span>
        </div>
        <h3 className="font-display text-lg font-bold leading-tight tracking-tight text-white">
          {product.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-400">
          {product.description}
        </p>

        {product.top_review?.comment && product.top_review.rating >= 4 && (
          <div
            data-testid={`top-review-${product.id}`}
            className="mt-3 flex gap-2 border-l-2 border-emerald-500/50 bg-emerald-500/5 px-3 py-2"
          >
            <Quote className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400/70" />
            <div className="min-w-0">
              <p className="line-clamp-2 text-xs italic leading-snug text-slate-300">
                "{product.top_review.comment}"
              </p>
              <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-wider text-slate-500">
                — {product.top_review.author}
              </span>
            </div>
          </div>
        )}

        {/* Pricing */}
        <div className="mt-5 flex items-end justify-between border-t border-white/10 pt-4">
          <div>
            <div className="flex items-baseline gap-2">
              <span
                data-testid={`price-${product.id}`}
                className="font-mono text-2xl font-bold text-emerald-400"
                style={{ textShadow: "0 0 20px rgba(0,255,102,0.4)" }}
              >
                {isFree ? "Free" : `$${product.base_price}`}
              </span>
              {!isFree && pricing.live_competitor > product.base_price && (
                <span
                  key={pricing.live_competitor}
                  data-testid={`market-price-${product.id}`}
                  className={`flex items-center gap-0.5 font-mono text-sm transition-colors duration-500 ${flashClass}`}
                >
                  {dir === "up" && <TrendingUp className="h-3 w-3" />}
                  {dir === "down" && <TrendingDown className="h-3 w-3" />}
                  <span className="line-through">${pricing.live_competitor}</span>
                </span>
              )}
            </div>
            {pricing.discount_pct > 0 && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-500">
                {pricing.discount_pct}% below market avg
              </span>
            )}
            {!isFree && history[product.id]?.length > 1 && (
              <div className="mt-1.5" data-testid={`sparkline-${product.id}`}>
                <Sparkline data={history[product.id]} up={dir !== "down"} />
              </div>
            )}
          </div>
          <button
            data-testid={`buy-btn-${product.id}`}
            onClick={buy}
            disabled={buying}
            className="flex items-center gap-1 bg-white px-3.5 py-2 text-sm font-semibold text-slate-950 transition-[background-color] duration-200 hover:bg-emerald-400 disabled:opacity-60"
          >
            {buying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>{isFree ? "Get" : "Buy"} <ArrowUpRight className="h-3.5 w-3.5" /></>}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
